// Setting → Product Group → หน้า config ของกลุ่ม (เต็มหน้า แบบเดียวกับสินค้า)
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useMutation } from "@tanstack/react-query";
import type { Brand, ProductGroup, WazzupProduct } from "@contracts/api";
import { IconSearch } from "@shared/ui/icons";
import { cn } from "@shared/lib/utils";
import { deleteGroup, updateBrand, updateGroup } from "../api/settings-api";
import {
  useEnabledBrands,
  useGroups,
  useInvalidateSettings,
  useProducts,
} from "../api/use-settings";
import { useCampaignCountLabel } from "../api/use-campaign-count";
import { useMemberCountLabel } from "../api/use-member-count";
import { BrandMembersSection } from "../components/brand-members-section";
import { CampaignsSection } from "../components/campaigns-section";
import { FeatureRows } from "../components/feature-rows";
import { MyJobSwitch } from "../components/my-job-switch";
import { PagePicker } from "../components/page-picker";
import { SectionCards } from "../components/section-cards";
import { autoGroupLogo, fileToLogoDataUrl, imageLoads } from "../lib/images";
import { groupTagline } from "../model/setting-menus";

const MEMBER_LIMIT = 60;

export function GroupDetailPage() {
  const { id = "" } = useParams();
  const { data: groups, isPending } = useGroups();
  const { data: products } = useProducts();
  const { byId: enabledBrands } = useEnabledBrands();

  const g = (groups ?? []).find((x) => x.id === id);

  if (isPending)
    return (
      <div className="panel">
        <p className="muted">กำลังโหลดข้อมูล…</p>
      </div>
    );

  if (!g)
    return (
      <>
        <div className="content-head">
          <div>
            <div className="breadcrumb">
              <Link to="/setting/group">Setting / Product Groups</Link>
            </div>
            <h1>ไม่พบกลุ่มสินค้า</h1>
          </div>
        </div>
        <div className="panel">
          <p className="muted">ไม่พบกลุ่มรหัส {id}</p>
          <Link className="btn-outline inline-btn" to="/setting/group">
            ← กลับไปรายการกลุ่ม
          </Link>
        </div>
      </>
    );

  // key = สลับไปกลุ่มอื่นให้เริ่มค่าในฟอร์มใหม่ทั้งชุด (แทนการ setState ใน effect)
  return (
    <GroupEditor key={g.id} group={g} brand={enabledBrands.get(g.id)} products={products ?? []} />
  );
}

function GroupEditor({
  group: g,
  brand,
  products,
}: {
  group: ProductGroup;
  brand: Brand | undefined;
  products: WazzupProduct[];
}) {
  const navigate = useNavigate();
  const invalidate = useInvalidateSettings();
  const campLabel = useCampaignCountLabel(g.id);
  const enabled = !!brand;
  const memberLabel = useMemberCountLabel(g.id, enabled);

  const [name, setName] = useState(g.name);
  const [members, setMembers] = useState<string[]>(g.members);
  const [customLogo, setCustomLogo] = useState<string | null>(g.logoUrl);
  const [status, setStatus] = useState("");

  const byCode = useMemo(() => new Map(products.map((p) => [p.productCode, p])), [products]);

  const save = useMutation({
    mutationFn: async () => {
      const logoUrl =
        customLogo ?? (await autoGroupLogo(members.map((c) => byCode.get(c)?.productLogoURL)));
      const saved = await updateGroup(g.id, { name: name.trim(), members, logoUrl });
      // ถ้า enable อยู่ ให้ชื่อ/คำอธิบาย/รูป ใน My Job ตามไปด้วย
      if (enabled)
        await updateBrand(g.id, {
          name: saved.name,
          tagline: groupTagline(saved),
          logoUrl: saved.logoUrl,
        });
      return saved;
    },
    onMutate: () => setStatus("กำลังบันทึก…"),
    onSuccess: () => {
      setStatus("บันทึกแล้ว");
      invalidate();
    },
    onError: () => setStatus("บันทึกไม่สำเร็จ กรุณาลองใหม่"),
  });

  const remove = useMutation({
    mutationFn: () => deleteGroup(g.id),
    onSuccess: () => {
      invalidate();
      navigate("/setting/group");
    },
  });

  const memberNames = members
    .map((c) => byCode.get(c)?.productEngName || `#${c}`)
    .slice(0, 3)
    .join(" · ");

  return (
    <>
      <div className="content-head">
        <div>
          <div className="breadcrumb">
            <Link to="/setting/group">Setting / Product Groups</Link> / {g.name}
          </div>
          <h1>Product Group settings</h1>
        </div>
        <Link className="btn-outline inline-btn" to="/setting/group">
          ← กลับ
        </Link>
      </div>

      <div className="cfg-hero panel">
        <GroupLogoPreview
          customLogo={customLogo}
          members={members}
          byCode={byCode}
          name={name || g.name}
          color={g.color}
        />
        <div className="grow">
          <div className="product-name">{g.name}</div>
          <div className="product-sub">
            {members.length
              ? memberNames + (members.length > 3 ? ` · +${members.length - 3}` : "")
              : "กลุ่มว่าง — ผูกเพจได้เลย"}
          </div>
          <div className="product-meta">
            <span className="code-chip">{g.id}</span>
            <span className="badge badge-gray">กลุ่มสินค้า</span>
          </div>
        </div>
        <MyJobSwitch
          enabled={enabled}
          payload={{
            id: g.id,
            kind: "group",
            name: g.name,
            tagline: groupTagline(g),
            color: g.color,
            logoUrl: g.logoUrl,
          }}
        />
      </div>

      <SectionCards
        sections={[
          {
            id: "info",
            title: "ข้อมูลกลุ่ม",
            desc: "ชื่อกลุ่ม รูป และสินค้าที่อยู่ในกลุ่ม",
            status: `${members.length} สินค้า`,
            body: (
              <>
                <div className="cfg-sub">
                  <label className="field-label">
                    ชื่อกลุ่ม
                    <input
                      type="text"
                      value={name}
                      maxLength={120}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </label>
                  <LogoActions
                    members={members}
                    byCode={byCode}
                    customLogo={customLogo}
                    onPick={setCustomLogo}
                  />
                </div>
                <div className="cfg-sub">
                  <MemberPicker products={products ?? []} members={members} onChange={setMembers} />
                </div>
                <div className="drawer-actions">
                  <button
                    type="button"
                    className="btn-primary"
                    disabled={save.isPending}
                    onClick={() => {
                      if (!name.trim()) {
                        setStatus("กรุณาตั้งชื่อกลุ่ม");
                        return;
                      }
                      save.mutate();
                    }}
                  >
                    บันทึกกลุ่ม
                  </button>
                  <span className="muted save-status">{status}</span>
                </div>
              </>
            ),
          },
          {
            id: "channels",
            title: "Channels & Inbox",
            desc: "ผูกเพจ Inbox Center และระบบที่เชื่อมต่อ",
            status: enabled ? "พร้อมตั้งค่า" : "เปิด My Job ก่อน",
            body: (
              <>
                <div className="cfg-sub">
                  <h4>Inbox Center (เมนู Messages)</h4>
                  {enabled ? (
                    <>
                      <p className="muted small-note">
                        เลือกเพจที่จะผูกกับกลุ่มนี้ — ผูกได้หลายเพจ ทั้ง Facebook และ LINE OA
                      </p>
                      <div className="page-picker">
                        <PagePicker brand={brand} />
                      </div>
                    </>
                  ) : (
                    <p className="muted">เปิดสวิตช์ My Job ด้านบนก่อน จึงจะผูกเพจกับกลุ่มนี้ได้</p>
                  )}
                </div>
                <div className="cfg-sub">
                  <h4>ดึงเนื้อหามาแสดงในเมนูไหน</h4>
                  {enabled ? (
                    <>
                      <p className="muted small-note">ปิดไว้ = เมนูนั้นจะไม่ขึ้นในหน้ากลุ่มนี้</p>
                      <FeatureRows brand={brand} />
                    </>
                  ) : (
                    <p className="muted">เปิดสวิตช์ My Job ด้านบนก่อน</p>
                  )}
                </div>
              </>
            ),
          },
          {
            id: "campaigns",
            title: "Campaigns",
            desc: "สร้างแคมเปญของกลุ่มนี้ — ชื่อจะไปแสดงใน dropdown หน้า My Job",
            status: campLabel,
            body: <CampaignsSection brandId={g.id} />,
          },
          {
            id: "access",
            title: "ผู้เข้าถึง",
            desc: "กำหนดว่าใครเห็นกลุ่มนี้ใน My Job ได้บ้าง",
            status: memberLabel,
            body: <BrandMembersSection brandId={g.id} enabled={enabled} />,
          },
        ]}
      />

      {/* ลบกลุ่ม — ไม่ใช่หัวข้อตั้งค่า และไม่ควรเด่นเท่ากัน จึงเป็นลิงก์เล็ก ๆ ท้ายหน้า */}
      <DeleteGroupButton busy={remove.isPending} onConfirm={() => remove.mutate()} />
    </>
  );
}

function GroupLogoPreview({
  customLogo,
  members,
  byCode,
  name,
  color,
}: {
  customLogo: string | null;
  members: string[];
  byCode: Map<string, WazzupProduct>;
  name: string;
  color: string;
}) {
  const [auto, setAuto] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (customLogo) return;
    let alive = true;
    void autoGroupLogo(members.map((c) => byCode.get(c)?.productLogoURL)).then((url) => {
      if (alive) setAuto(url);
    });
    return () => {
      alive = false;
    };
  }, [customLogo, members, byCode]);

  const url = customLogo || auto;
  return (
    <div className="product-logo lg" id="group-logo-preview">
      {url && !failed && <img src={url} alt="" onError={() => setFailed(true)} />}
      <span
        className="logo-fallback"
        style={url && !failed ? undefined : { display: "grid", background: color, color: "#fff" }}
      >
        {(name || "?").charAt(0)}
      </span>
    </div>
  );
}

function LogoActions({
  members,
  byCode,
  customLogo,
  onPick,
}: {
  members: string[];
  byCode: Map<string, WazzupProduct>;
  customLogo: string | null;
  onPick: (url: string | null) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [note, setNote] = useState("");
  const [choices, setChoices] = useState<WazzupProduct[] | null>(null);
  const [checking, setChecking] = useState(false);

  const showChoices = async () => {
    if (choices) {
      setChoices(null);
      return;
    }
    if (!members.length) {
      setChoices([]);
      return;
    }
    setChecking(true);
    const list = members.map((c) => byCode.get(c)).filter((p): p is WazzupProduct => !!p);
    const ok = await Promise.all(list.map((p) => imageLoads(p.productLogoURL)));
    setChoices(list.filter((_, i) => ok[i]));
    setChecking(false);
  };

  return (
    <>
      <div className="logo-actions">
        <button
          type="button"
          className="btn-outline btn-sm"
          onClick={() => fileRef.current?.click()}
        >
          ⬆︎ อัปโหลดรูป
        </button>
        <button type="button" className="btn-outline btn-sm" onClick={() => void showChoices()}>
          เลือกจากสินค้าในกลุ่ม
        </button>
        <button
          type="button"
          className="btn-outline btn-sm"
          onClick={() => {
            onPick(null);
            setChoices(null);
            setNote("ใช้รูปอัตโนมัติจากสินค้าในกลุ่ม");
          }}
        >
          ใช้รูปอัตโนมัติ
        </button>
        <input
          type="file"
          accept="image/*"
          hidden
          ref={fileRef}
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            try {
              onPick(await fileToLogoDataUrl(file));
              setNote("ใช้รูปที่กำหนดเอง");
            } catch (err) {
              setNote((err as Error).message);
            }
            e.target.value = "";
          }}
        />
      </div>
      <p className="muted small-note">{note || (customLogo ? "ใช้รูปที่กำหนดเอง" : "")}</p>
      <div className="logo-choices" hidden={!choices}>
        {checking ? (
          <p className="muted small-note">กำลังตรวจรูปของสินค้าในกลุ่ม…</p>
        ) : !members.length ? (
          <p className="muted small-note">ยังไม่มีสินค้าในกลุ่ม</p>
        ) : choices?.length ? (
          choices.map((p) => (
            <button
              key={p.productCode}
              type="button"
              className={cn("logo-choice", customLogo === p.productLogoURL && "active")}
              title={p.productEngName || ""}
              onClick={() => {
                onPick(p.productLogoURL ?? null);
                setChoices(null);
                setNote("ใช้รูปที่กำหนดเอง");
              }}
            >
              <img src={p.productLogoURL} alt="" />
              <span className="code-chip">#{p.productCode}</span>
            </button>
          ))
        ) : (
          <p className="muted small-note">สินค้าในกลุ่มไม่มีรูปที่ใช้ได้เลย — อัปโหลดรูปเองได้</p>
        )}
      </div>
    </>
  );
}

function MemberPicker({
  products,
  members,
  onChange,
}: {
  products: WazzupProduct[];
  members: string[];
  onChange: (next: string[]) => void;
}) {
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();

  const chosen = products.filter((p) => members.includes(p.productCode));
  // ผลค้นหาแสดงเฉพาะที่ยังไม่ได้เลือก และต้องพิมพ์ค้นหาก่อน (สินค้ามี ~700 รายการ)
  const rest = products.filter(
    (p) =>
      !members.includes(p.productCode) &&
      p.productActive === "Y" &&
      needle &&
      [p.productCode, p.productEngName, p.productThName, p.brandEngName, p.clientEngName].some(
        (v) => (v || "").toLowerCase().includes(needle),
      ),
  );
  const matches = rest.slice(0, MEMBER_LIMIT);

  const row = (p: WazzupProduct) => (
    <label className="member-row" key={p.productCode}>
      <input
        type="checkbox"
        checked={members.includes(p.productCode)}
        onChange={(e) =>
          onChange(
            e.target.checked
              ? [...members, p.productCode]
              : members.filter((c) => c !== p.productCode),
          )
        }
      />
      <span className="grow">
        <span className="provider-name">{p.productEngName || "-"}</span>
        <span className="provider-status muted">
          {p.brandEngName || "-"} · {p.clientEngName || "-"}
        </span>
      </span>
      <span className="code-chip">#{p.productCode}</span>
    </label>
  );

  return (
    <>
      <h4>
        สินค้าในกลุ่ม <span className="muted">{members.length} สินค้า</span>
      </h4>
      <div className="search-box" style={{ marginBottom: 8 }}>
        <IconSearch />
        <input
          type="text"
          placeholder="ค้นหาสินค้าเพื่อเพิ่ม..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      <div className="picker-actions">
        <button
          type="button"
          className="btn-outline btn-sm"
          disabled={!matches.length}
          onClick={() => onChange([...members, ...matches.map((p) => p.productCode)])}
        >
          {matches.length ? `เลือกทั้งหมด (${matches.length})` : "เลือกทั้งหมด"}
        </button>
        <button type="button" className="btn-outline btn-sm" onClick={() => onChange([])}>
          ล้างที่เลือก
        </button>
        <span className="muted">
          {matches.length
            ? rest.length > MEMBER_LIMIT
              ? `พบ ${rest.length} รายการ แสดง/เลือกได้ ${MEMBER_LIMIT} รายการแรก`
              : ""
            : needle
              ? ""
              : "ค้นหาก่อนจึงจะเลือกทั้งหมดได้"}
        </span>
      </div>
      <div className="member-picker">
        {chosen.map(row)}
        {matches.map(row)}
        {!chosen.length && !matches.length && (
          <p className="muted" style={{ padding: "8px 4px" }}>
            {needle ? "ไม่พบสินค้าที่ค้นหา" : "พิมพ์ค้นหาเพื่อเพิ่มสินค้าเข้ากลุ่ม"}
          </p>
        )}
      </div>
      <p className="muted small-note">
        กลุ่มไม่จำเป็นต้องมีสินค้า — ใช้เป็นกล่องผูกเพจล้วน ๆ ก็ได้
      </p>
    </>
  );
}

/** ต้องกดสองครั้ง และปลดอาวุธเองถ้าไม่กดต่อ — ไม่ให้ค้างสถานะพร้อมลบไว้รอคลิกหลง */
function DeleteGroupButton({ busy, onConfirm }: { busy: boolean; onConfirm: () => void }) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 5000);
    return () => clearTimeout(t);
  }, [armed]);

  return (
    <div className="page-foot-danger">
      <button
        type="button"
        className="link-danger"
        disabled={busy}
        onClick={() => (armed ? onConfirm() : setArmed(true))}
      >
        {armed ? "กดอีกครั้งเพื่อยืนยันลบ" : "ลบกลุ่มนี้"}
      </button>
      <span className="muted small-note">การลบไม่กระทบสินค้าที่อยู่ในกลุ่ม</span>
    </div>
  );
}
